// =====================================================
// ACTIVITY API CLIENT  (Admin Activity Dashboard)
// Thin wrapper around the /api/activity endpoints. Owns:
//   • BASE_URL detection (same rule the rest of the app uses)
//   • the per-login sessionId (generated once, kept in localStorage)
//   • the authenticated axios instance for admin reads
//   • heartbeat + endSession helpers (endSession also works via sendBeacon so
//     it survives a tab close)
//
// Nothing here throws in a way that reaches the UI — tracking is best-effort by
// design and must never disrupt the app.
// =====================================================

import axios from "axios";
import { readPermission } from "./notificationPermission";

export const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const SESSION_KEY = "activitySessionId";
const LOGIN_AT_KEY = "activityLoginAt";

// A dependency-free unique id (crypto.randomUUID where available, else a
// timestamp+random fallback). One per login, shared across tabs in the browser.
function generateId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return (
    "s-" +
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 10)
  );
}

// Returns the current session id, creating one (and stamping the login time) on
// first use after a login. Stable across refreshes for the same login.
export function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = generateId();
    localStorage.setItem(SESSION_KEY, id);
    localStorage.setItem(LOGIN_AT_KEY, new Date().toISOString());
  }
  return id;
}

export function getLoginAt() {
  return localStorage.getItem(LOGIN_AT_KEY) || new Date().toISOString();
}

// Wipe the session identity — called on logout so the next login opens a fresh
// session document.
export function clearSessionId() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LOGIN_AT_KEY);
}

const token = () => localStorage.getItem("token");

// ── HEARTBEAT ──
// Best-effort keep-alive. `interacted` marks whether real user activity
// happened since the last beat (drives idle-time).
export async function sendHeartbeat({ page, route, interacted }) {
  const t = token();
  if (!t) return;
  try {
    await axios.post(
      `${BASE_URL}/api/activity/heartbeat`,
      {
        sessionId: getSessionId(),
        page,
        route,
        loginTime: getLoginAt(),
        interacted: !!interacted,
        // Refer & Earn: the browser's notification permission RIGHT NOW, read
        // fresh on every beat so a permission revoked in Settings is reported
        // on the next one. The server stamps its own time on it.
        notificationPermission: readPermission(),
      },
      { headers: { Authorization: `Bearer ${t}` } }
    );
  } catch {
    // Swallow — tracking must never disturb the app.
  }
}

// ── END SESSION ──
// Prefer sendBeacon so it still fires during tab close / navigation away; fall
// back to a keepalive fetch. Auth header can't ride a beacon, but the /end
// route only needs the opaque sessionId (closing your own session is harmless).
export function endSession() {
  const id = localStorage.getItem(SESSION_KEY);
  if (!id) return;
  const url = `${BASE_URL}/api/activity/end`;
  const payload = JSON.stringify({ sessionId: id });

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // ignore
  }
}

// ── ADMIN READS ──
// Authenticated axios instance for the dashboard. Created per-call so it always
// picks up the freshest token.
function adminApi() {
  return axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${token()}` },
  });
}

export const adminActivity = {
  overview: () => adminApi().get("/api/activity/admin/overview").then((r) => r.data),
  online: () => adminApi().get("/api/activity/admin/online").then((r) => r.data),
  logins: (params) =>
    adminApi().get("/api/activity/admin/logins", { params }).then((r) => r.data),
  visits: (params) =>
    adminApi().get("/api/activity/admin/visits", { params }).then((r) => r.data),
  pageStats: () =>
    adminApi().get("/api/activity/admin/page-stats").then((r) => r.data),
  userTimeline: (id, params) =>
    adminApi()
      .get(`/api/activity/admin/user/${id}/timeline`, { params })
      .then((r) => r.data),
};

export default adminActivity;
